import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const MAX_MB = 10;

export default function CaseAttachments({ caseId, attachments, canUpload, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) return toast.error(`File exceeds ${MAX_MB}MB`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/checks/${caseId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Consent form uploaded");
      onChanged && (await onChanged());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const download = async (att) => {
    try {
      const resp = await api.get(`/checks/${caseId}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Consent forms & attachments</div>
        {canUpload && (
          <>
            <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid="attachment-file-input" />
            <Button size="sm" onClick={pick} disabled={busy} data-testid="upload-consent-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              <Upload size={14} className="mr-1.5" /> {busy ? "Uploading…" : "Upload consent"}
            </Button>
          </>
        )}
      </div>
      {attachments && attachments.length ? (
        <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center justify-between px-3 py-2 bg-white" data-testid={`attachment-row-${a.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip size={14} className="text-slate-400 shrink-0" />
                <span className="text-sm text-slate-800 truncate">{a.original_filename}</span>
                <span className="text-xs text-slate-400 shrink-0">{Math.round((a.size || 0) / 1024)} KB</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => download(a)} className="rounded-full text-slate-600 hover:text-[#1E3A8A]" data-testid={`download-attachment-${a.id}`}>
                <Download size={14} />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg p-4 text-center">
          {canUpload ? "No consent form uploaded yet. Accepted: PDF, PNG, JPEG, WEBP (max 10MB)." : "No attachments uploaded by the client yet."}
        </div>
      )}
    </div>
  );
}
